import React, { useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'

import styled from 'styled-components'
import { Rnd } from 'react-rnd'

import { Region } from './Region'
import { RegionType } from './RegionType'
import { useRegionCreator } from './useRegionCreator'

export const Designer = ({
  moduleName,
  height,
  width,
  regions: _regions,
  onRegionsChanged
}) => {
  const containerRef = useRef()

  const regions = useMemo(() => {
    return _regions.map(region => {
      return {
        ...region,
        height: region.height * height,
        width: region.width * width,
        x: region.x * width,
        y: region.y * height
      }
    })
  }, [_regions, height, width])

  const getChangeHandler = i => nextRegion => {
    const nextRegions = [..._regions]

    if (nextRegion == null) {
      nextRegions.splice(i, 1)
    } else {
      // make sure temporary stuff from editing is cleared away before saving
      if (nextRegion.regionType === RegionType.ANCHOR_POINT) {
        delete nextRegion.field
      } else {
        delete nextRegion.value
      }
      delete nextRegion.previousField
      delete nextRegion.previousValue
      nextRegions[i] = {
        ...nextRegion,
        width: _.round(nextRegion.width / width, 4),
        height: _.round(nextRegion.height / height, 4),
        x: _.round(nextRegion.x / width, 4),
        y: _.round(nextRegion.y / height, 4)
      }
    }

    onRegionsChanged(nextRegions)
  }

  const dimensionsSet = height > 0 && width > 0

  const creator = useRegionCreator(
    moduleName,
    containerRef,
    getChangeHandler(_regions.length)
  )

  return (
    <Container
      ref={containerRef}
      onMouseDown={creator.onMouseDown}
      onMouseMove={creator.onMouseMove}
      onMouseUp={creator.onMouseUp}
    >
      {dimensionsSet &&
        regions.map((region, i) => {
          return (
            <Region
              key={i}
              region={region}
              onRegionChanged={getChangeHandler(i)}
              moduleName={moduleName}
            >
              {region.field}
            </Region>
          )
        })}

      {creator.prototype && (
        <Rnd
          bounds="parent"
          position={creator.prototype}
          size={creator.prototype}
          style={boxStyle}
        />
      )}
    </Container>
  )
}

Designer.propTypes = {
  moduleName: PropTypes.string.isRequired,
  height: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  regions: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string,
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
      width: PropTypes.number.isRequired,
      height: PropTypes.number.isRequired
    })
  ).isRequired,
  onRegionsChanged: PropTypes.func.isRequired
}

const Container = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`

const boxStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'solid 1px black',
  backgroundColor: 'rgba(150, 150, 150, 0.2)'
}
